'use client';

import React from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@unity/cloud-ui';
import { useTheme } from '@unity/cloud-ui/styles';

export interface ListGroupProps {
  title?: string;
  collapsible?: boolean;
}

export const ListGroup = (props: React.PropsWithChildren<ListGroupProps>) => {
  const { title, collapsible = false, children } = props;
  const [expanded, setExpanded] = React.useState(true);
  const { vars, spacing } = useTheme();

  const expandIcon = collapsible ? <CaretDown /> : '';

  function handleAccordionClick() {
    if (collapsible) {
      setExpanded((prev) => !prev);
    }
  }

  return (
    <Accordion
      defaultExpanded={true}
      expanded={expanded}
      disableGutters
      sx={{
        border: `1px solid ${vars.palette.divider}`,
        borderRadius: '4px',
        '& > .MuiAccordion-heading .MuiAccordionSummary-expandIconWrapper': {
          transform: 'rotate(-180deg)',
        },
      }}
      onChange={handleAccordionClick}
    >
      {collapsible ? (
        <AccordionSummary
          expandIcon={expandIcon}
          sx={{
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px',
            '.MuiAccordionSummary-content.Mui-expanded': {
              margin: spacing(1.5, 0),
            },
            '&.Mui-expanded': {
              minHeight: '48px !important',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
            borderBottomLeftRadius: '3px',
            borderBottomRightRadius: '3px',
            '&:hover': {
              backgroundColor: vars.palette.primary.lighter,
            },
          }}
        >
          <Typography variant="h5" component="div">
            {title}
          </Typography>
        </AccordionSummary>
      ) : (
        <Box component="div" py={1.5} px={2} borderBottom={`1px solid ${vars.palette.divider}`} fontWeight="bold">
          {title}
        </Box>
      )}

      <AccordionDetails
        sx={{
          border: 'none',
          p: 0,
        }}
      >
        <Box
          component="ul"
          sx={{
            listStyleType: 'none',
            marginBlockStart: 0,
            marginBlockEnd: 0,
            paddingInlineStart: 0,
            '& > li': {
              padding: 2,
              margin: 0,
              '&:nth-of-type(odd)': {
                backgroundColor: (theme) => theme.vars.palette.action.hover,
              },
              '&:nth-of-type(even)': {
                backgroundColor: (theme) => theme.vars.palette.common.inverseWhite,
              },
            },
          }}
        >
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
